
import { useState, useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { BlogCard } from "@/components/blog/BlogCard";
import { getAllPosts, getPostsByCategory } from "@/integrations/sanity/client";
import { Badge } from "@/components/ui/badge";

// Example categories - you'll fetch these from Sanity in a real app
const categories = ["Nutrition", "Fitness", "Supplements", "Mindfulness", "Sleep"];

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const postsPerPage = 9;

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        let postsData;
        
        if (selectedCategory) {
          postsData = await getPostsByCategory(selectedCategory, postsPerPage, 0);
        } else {
          postsData = await getAllPosts(postsPerPage, 0);
        }
        
        setPosts(postsData);
        setPage(1);
        setHasMore(postsData.length === postsPerPage);
      } catch (error) {
        console.error("Error fetching blog data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPosts();
  }, [selectedCategory]);

  const loadMorePosts = async () => {
    try {
      let nextPosts;
      
      if (selectedCategory) {
        nextPosts = await getPostsByCategory(
          selectedCategory, 
          postsPerPage, 
          page * postsPerPage
        );
      } else {
        nextPosts = await getAllPosts(postsPerPage, page * postsPerPage);
      }
      
      if (nextPosts.length === 0) {
        setHasMore(false);
        return;
      }
      
      setPosts(prev => [...prev, ...nextPosts]);
      setPage(prev => prev + 1);
      setHasMore(nextPosts.length === postsPerPage);
    } catch (error) {
      console.error("Error fetching more posts:", error);
    }
  };

  const handleCategorySelect = (category: string | null) => {
    if (category === selectedCategory) {
      setSelectedCategory(null);
    } else {
      setSelectedCategory(category);
    }
  };

  return (
    <>
      <Navbar />
      <div className="container mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-display font-bold tracking-tight mb-4">
            Wellness Insights
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Discover the latest in nutrition, fitness, supplements, mindfulness, and sleep science.
          </p>

          <div className="flex flex-wrap justify-center gap-2 mt-6">
            <Badge 
              variant={selectedCategory === null ? "default" : "outline"}
              className="cursor-pointer px-3 py-1"
              onClick={() => handleCategorySelect(null)}
            >
              All
            </Badge>
            {categories.map((category) => (
              <Badge
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                className="cursor-pointer px-3 py-1"
                onClick={() => handleCategorySelect(category)}
              >
                {category}
              </Badge>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-muted rounded-lg h-80"></div>
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post) => (
                <BlogCard key={post._id} post={post} />
              ))}
            </div>

            {posts.length === 0 && (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No posts found.</p>
              </div>
            )}

            {hasMore && (
              <div className="flex justify-center mt-10">
                <Button onClick={loadMorePosts} variant="outline">
                  Load More
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
